// Museum names from the artworks' `located_in` field, cleaned for museum hub
// pages: entities decoded, variant spellings merged, and values that are not
// one identifiable institution left out. Pure functions, shared by the static
// build and the app so both link the same names.

// "Musée d&amp;#39;Orsay" -> "Musée d'Orsay". Some values are encoded twice.
export function decodeEntities(value: string): string {
  let s = value;
  for (let i = 0; i < 3 && /&amp;/i.test(s); i++) s = s.replace(/&amp;/gi, "&");
  return s
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(parseInt(n, 10)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCodePoint(parseInt(n, 16)))
    .replace(/&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&amp;/gi, "&");
}

// Decoded, curly apostrophes straightened, spaces collapsed, trailing comma dropped.
export function tidyMuseumName(value: unknown): string {
  if (typeof value !== "string") return "";
  return decodeEntities(value)
    .replace(/[‘’ʼ]/g, "'")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/,$/, "")
    .trim();
}

// Accent- and case-insensitive form. Only names equal in this form are merged,
// so "Gemäldegalerie" and "Gemäldegalerie Alte Meister" stay apart.
export function foldMuseumName(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

// Variant -> canonical name, for variants that differ by more than accents.
export const MUSEUM_ALIASES: Record<string, string> = {
  "Philips Collection": "Phillips Collection",
  "The Phillips Collection": "Phillips Collection",
  "Städelsches Kunstinstitut und Städtische Galerie (Städel)": "Städel",
  "Städelsches Kunstinstitut und Städtische Galerie": "Städel",
  "Roerich Museum": "Nicholas Roerich Museum",
  "Museum of Modern Art": "Museum of Modern Art (MoMA)",
  "Museum of Russian Art": "Museum of Russian Art (Tereshchenko Museum)",
};

// Values that are a place, not an institution.
export const PLACE_NAMES = new Set([
  "france", "italy", "spain", "germany", "netherlands", "the netherlands", "belgium", "austria",
  "switzerland", "united kingdom", "uk", "england", "scotland", "wales", "ireland", "portugal",
  "greece", "russia", "ukraine", "poland", "czech republic", "hungary", "romania", "norway",
  "sweden", "denmark", "finland", "united states", "usa", "us", "canada", "mexico", "brazil",
  "argentina", "japan", "china", "india", "australia",
  "london", "paris", "rome", "florence", "venice", "milan", "naples", "madrid", "barcelona",
  "berlin", "munich", "dresden", "frankfurt", "vienna", "amsterdam", "brussels", "prague",
  "budapest", "moscow", "st. petersburg", "saint petersburg", "oslo", "stockholm", "copenhagen",
  "helsinki", "new york", "new york city", "washington", "washington, d.c.", "washington dc",
  "chicago", "boston", "philadelphia", "los angeles", "mexico city", "tokyo", "kyiv", "kiev",
]);

// Names that in our data cover more than one institution, so one hub would mix
// different museums.
export const AMBIGUOUS_MUSEUMS = new Set([
  "national gallery", // London and Oslo
  "national museum", // several countries' national museums
  "museum of fine arts", // Ghent and Budapest, not Boston
  "national portrait gallery", // London and Washington
  "private collection", // many owners, not one institution
]);

const ALIASES = new Map(Object.entries(MUSEUM_ALIASES).map(([k, v]) => [foldMuseumName(k), v]));

// The name to show for a located_in value, or "" if it is empty. Excluded
// values are still returned; use museumKey to decide whether to link.
export function museumDisplayName(value: unknown): string {
  const name = tidyMuseumName(value);
  return ALIASES.get(foldMuseumName(name)) ?? name;
}

// The key works are grouped and looked up by: "" for values that get no hub.
export function museumKey(value: unknown): string {
  const name = museumDisplayName(value);
  const key = foldMuseumName(name);
  if (!key || PLACE_NAMES.has(key) || AMBIGUOUS_MUSEUMS.has(key)) return "";
  return key;
}

// located_in is usually a string, occasionally a list.
export function museumValues(value: unknown): string[] {
  const list = Array.isArray(value) ? value : [value];
  return list.map(museumDisplayName).filter(Boolean);
}
